import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CalendarDays, Stethoscope, HeartPulse, ListChecks, MessageCircle, Clock3, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import VisitsTable from "@/components/portal/VisitsTable";
import { PageHeader, Panel, StatCard, Spinner, btnSecondary, btnGhost } from "@/components/portal/ui";

function PendingList({ title, items, kind, onDone }) {
  const approve = async (p) => {
    try {
      await api.put(`/${kind}/${p.id}`, { status: "approved" });
      toast.success(`${p.first_name} ${p.last_name} approvato`);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Panel data-testid={`pending-${kind}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold">{title}</h3>
        <Link to={`/admin/${kind === "doctors" ? "medici" : "infermieri"}`} className="text-xs font-semibold text-[#5B2C91] hover:underline">Vedi tutti</Link>
      </div>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-[#8A809A]">Nessuna candidatura in attesa.</p>
      ) : (
        <ul className="mt-3 divide-y divide-[#5B2C91]/8">
          {items.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`pending-item-${p.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{p.first_name} {p.last_name}</p>
                <p className="truncate text-xs text-[#6B6280]">{p.email}{p.city ? ` · ${p.city}` : ""}</p>
              </div>
              <button onClick={() => approve(p)} className={btnGhost} data-testid={`pending-approve-${p.id}`}>
                <UserCheck className="h-3.5 w-3.5" /> Approva
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  if (!data) return <div className="flex justify-center py-20"><Spinner /></div>;
  const s = data.stats;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader
        eyebrow="Dashboard"
        title="Panoramica"
        desc="Visite, medici, infermieri e canale WhatsApp in un colpo d'occhio."
        actions={<Link to="/admin/visite" className={btnSecondary} data-testid="dashboard-new-visit-link">Gestisci visite</Link>}
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Visite oggi" value={s.visits_today} icon={CalendarCheck} testid="stat-visits-today" />
        <StatCard label="Prossimi 7 giorni" value={s.visits_week} icon={CalendarDays} color="#0EA5E9" testid="stat-visits-week" />
        <StatCard label="Richieste da confermare" value={s.visits_requested} icon={Clock3} color="#FF8A5B" testid="stat-visits-requested" />
        <StatCard label="Specialità attive" value={s.specialties_active} icon={ListChecks} color="#10B981" testid="stat-specialties" />
        <StatCard label="Medici attivi" value={s.doctors_approved} hint={`${s.doctors_pending} in attesa`} icon={Stethoscope} testid="stat-doctors" />
        <StatCard label="Infermieri attivi" value={s.nurses_approved} hint={`${s.nurses_pending} in attesa`} icon={HeartPulse} color="#0EA5E9" testid="stat-nurses" />
        <StatCard label="Messaggi WhatsApp" value={s.whatsapp_messages_in} hint="ricevuti" icon={MessageCircle} color="#25D366" testid="stat-whatsapp" />
        <Panel className="flex flex-col justify-between" data-testid="stat-whatsapp-status">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#8A809A]">Canale WhatsApp</p>
          <div className="mt-2 flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${data.whatsapp.active ? "bg-emerald-500" : "bg-amber-400"}`} />
            <span className="text-sm font-semibold">{data.whatsapp.active ? "Attivo sul sito" : "Non attivo"}</span>
          </div>
          <p className="mt-1 text-xs text-[#6B6280]">{data.whatsapp.configured ? `Cloud API configurata · ${data.whatsapp.display_phone}` : "Cloud API da configurare"}</p>
          <Link to="/admin/whatsapp" className="mt-3 text-xs font-semibold text-[#5B2C91] hover:underline">Configura →</Link>
        </Panel>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold">Prossime visite</h2>
            <Link to="/admin/visite" className="text-xs font-semibold text-[#5B2C91] hover:underline">Tutte le visite</Link>
          </div>
          <VisitsTable visits={data.upcoming_visits} compact emptyTitle="Nessuna visita in programma" emptyDesc="Crea la prima visita dalla sezione Visite." />
        </div>
        <div className="flex flex-col gap-6">
          <PendingList title="Medici in attesa" items={data.pending_doctors} kind="doctors" onDone={load} />
          <PendingList title="Infermieri in attesa" items={data.pending_nurses} kind="nurses" onDone={load} />
        </div>
      </div>
    </div>
  );
}
